import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Stack, CaretDown } from "@phosphor-icons/react";

interface MapHeaderProps {
  user: any;
  currentLang: "ro" | "en";
  theme: string;
  onThemeChange: (theme: string) => void;
  themes: Array<{ id: string; name: string; icon: string }>;
}

export function MapHeader({
  user: _user,
  currentLang,
  theme,
  onThemeChange,
  themes,
}: MapHeaderProps) {
  const [showThemeSelector, setShowThemeSelector] = useState(false);
  const themeButtonRef = useRef<HTMLButtonElement>(null);

  return (
    <>
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <img 
            src="/suuper_pin.svg" 
            alt="Suuper" 
            className="h-8 w-auto"
          />
          <p className="text-primary  text-2xl font-semibold">
            {currentLang === "ro" ? "Locații" : "Places"}
          </p>
        </div>

        <div className="flex items-center gap-2">
          {/* Theme selector */}
          <div className="relative">
            <button
              ref={themeButtonRef}
              onClick={() => setShowThemeSelector(!showThemeSelector)}
              className="p-2 bg-white/20 rounded-lg flex items-center gap-1 relative z-[2000]"
              title={currentLang === "ro" ? "Temă hartă" : "Map theme"}
            >
              <Stack size={20} />
              <CaretDown size={16} />
            </button>

            {showThemeSelector &&
              createPortal(
                <AnimatePresence>
                  <motion.div
                    key="overlay"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="fixed inset-0"
                    style={{ zIndex: 9999 }}
                    onClick={() => setShowThemeSelector(false)}
                  />
                  <motion.div
                    key="dropdown"
                    initial={{ opacity: 0, scale: 0.95, y: -10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="fixed bg-white rounded-xl shadow-lg border border-border min-w-[150px]"
                    style={{
                      zIndex: 10000,
                      top: themeButtonRef.current
                        ? themeButtonRef.current.getBoundingClientRect().bottom + 8
                        : 0,
                      right: themeButtonRef.current
                        ? window.innerWidth - themeButtonRef.current.getBoundingClientRect().right
                        : 0,
                    }}
                  >
                    {themes.map((themeOption) => (
                      <button
                        key={themeOption.id}
                        onClick={() => {
                          onThemeChange(themeOption.id);
                          setShowThemeSelector(false);
                        }}
                        className={`w-full px-4 py-3 text-left flex items-center gap-2 hover:bg-gray-50 first:rounded-t-xl last:rounded-b-xl ${
                          theme === themeOption.id ? "bg-primary/10 text-primary" : "text-text"
                        }`}
                      >
                        <span>{themeOption.icon}</span>
                        <span className="font-medium">{themeOption.name}</span>
                        {theme === themeOption.id && (
                          <span className="ml-auto text-primary">✓</span>
                        )}
                      </button>
                    ))}
                  </motion.div>
                </AnimatePresence>,
                document.body
              )}
          </div>
        </div>
      </div>
    </>
  );
}
